import {
  adminAgentsPath,
  adminHealthPath,
  adminRootPath,
  aiAgentsPath,
  aiDevicesPath,
  aiLogsPath,
  aiSessionsPath,
  equabotzSettingsPath,
  fleetObserverPath,
  fleetTrackerPath,
  primePath,
} from '@logic'
import { FLEET_NATIVE_PANELS, FleetNativePanelId } from './fleet-tracker-config'

export interface FleetNavigationItem {
  title: string
  route: string
  description: string
  badge?: string
  panel?: FleetNativePanelId
  commandCenter?: boolean
  commandCenterTab?: CommandCenterTab
}

export interface FleetNavigationGroup {
  title: string
  items: FleetNavigationItem[]
}

const DEFAULT_PANEL: FleetNativePanelId = 'agents'
type CommandCenterTab = 'threads' | 'fleet' | 'status'

export function isFleetIframePanelId(value: string | null | undefined): value is FleetNativePanelId {
  return FLEET_NATIVE_PANELS.some((panel) => panel.id === value)
}

export function getFleetPanelFromSearch(search: string): FleetNativePanelId {
  const params = new URLSearchParams(search)
  const panel = params.get('panel')
  return isFleetIframePanelId(panel) ? panel : DEFAULT_PANEL
}

export function hasValidFleetPanelSearch(search: string): boolean {
  return isFleetIframePanelId(new URLSearchParams(search).get('panel'))
}

export function buildFleetObserverPanelRoute(panel: FleetNativePanelId, search = ''): string {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  params.set('panel', panel)
  const nextSearch = params.toString()
  return `${fleetObserverPath}${nextSearch ? `?${nextSearch}` : ''}`
}

export function buildFleetCommandCenterRoute(search = '', pathname = fleetTrackerPath, tab: CommandCenterTab = 'fleet'): string {
  const currentSearch = search ? (search.startsWith('?') ? search : `?${search}`) : ''
  const params = new URLSearchParams({
    openEquanaut: 'true',
    commandCenter: 'true',
    commandCenterTab: tab,
    returnUrl: `${pathname}${currentSearch}`,
  })

  return `${primePath}?${params.toString()}`
}

export function buildFleetNativePanelRoute(panel: FleetNativePanelId, search = '', pathname = fleetObserverPath): string {
  switch (panel) {
    case 'agents':
      return aiAgentsPath
    case 'pipelines':
      return `${fleetTrackerPath}#spequafy-pipeline`
    case 'threads':
      return buildFleetCommandCenterRoute(search, pathname, 'threads')
    case 'cursor':
      return aiSessionsPath
    case 'fleet':
      return aiLogsPath
    case 'comet':
      return aiDevicesPath
    case 'command-center':
      return buildFleetCommandCenterRoute(search, pathname, 'fleet')
    default:
      return fleetTrackerPath
  }
}

const nativeSurfaceItems: FleetNavigationItem[] = FLEET_NATIVE_PANELS.map((panel) => ({
  title: panel.label,
  route: buildFleetNativePanelRoute(panel.id),
  description: `Native Equa surface for ${panel.label}.`,
  badge: 'Equa',
  panel: panel.id,
}))

export const FLEET_NAVIGATION_GROUPS: FleetNavigationGroup[] = [
  {
    title: 'Operations',
    items: [
      {
        title: 'Fleet Dashboard',
        route: fleetTrackerPath,
        description: 'Native live Command Center fleet dashboard route: /fleet',
        badge: 'Live',
      },
    ],
  },
  {
    title: 'Native Surfaces',
    items: nativeSurfaceItems,
  },
  {
    title: 'Related Surfaces',
    items: [
      {
        title: 'Equabotz Settings',
        route: equabotzSettingsPath,
        description: 'Gateway, skills, instances, and connection settings: /equabotz/settings',
      },
      {
        title: 'AI Agents',
        route: aiAgentsPath,
        description: 'Agent profile management route: /ai/profiles',
      },
      {
        title: 'Admin Agents',
        route: adminAgentsPath,
        description: 'Site admin agent monitor route: /admin/agents',
      },
      {
        title: 'System Health',
        route: adminHealthPath,
        description: 'Admin health route: /admin/health',
      },
      {
        title: 'Site Admin',
        route: adminRootPath,
        description: 'Site admin dashboard route: /admin',
      },
    ],
  },
]
